import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { colors } from '../../assets/colors';
import { RowView, Text } from '../../helpers';
import RoundBox from '../../components/RoundBox/RoundBox';
import { IInvoice } from '../../interfaces';
import ResursLogo from './ResursLogo';

type PaymentMethod = IInvoice['payment_methods'][number];

type Props = {
    totalAmount: number;
    selectedId: string | null;
    paymentMethods: PaymentMethod[];
    onSelect: (id: string) => void;
};

const specificTypeLabel: Record<string, string> = {
    COMPINVOICE: 'Företagsfaktura',
    INVOICE: 'Faktura',
    PART_PAYMENT: 'Delbetalning',
    PSPCARD: 'Kortbetalning',
    CARD: 'Kortbetalning'
};

function getMethodLabel(pm: PaymentMethod): string {
    return specificTypeLabel[pm.specificType] ?? pm.description;
}

interface OptionProps {
    selected: boolean;
}

const Option = styled.div<OptionProps>`
    background: ${(props) => (props.selected ? colors.faded : colors.faded)};
    padding: 18px 24px;
    border-radius: 6px;
    margin-top: 2vh;
    opacity: ${(props) => (props.selected ? 1 : 0.7)};
    border: ${(props) => (props.selected ? `2px solid ${colors.main}` : `1px solid ${colors.grey}`)};
    box-sizing: border-box;
    cursor: pointer;
    &:hover {
        opacity: 1;
    }
`;

const Dot = styled.div<OptionProps>`
    display: inline-block;
    height: 20px;
    width: 20px;
    min-width: 20px;
    margin-right: 12px;
    border-width: ${(props) => (props.selected ? '6px' : '1px')};
    border-style: solid;
    border-color: ${(props) => (props.selected ? colors.main : colors.grey)};
    border-radius: 100%;
`;

const LegalLinks = styled.ul`
    list-style: none;
    margin: 8px 0 0 32px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const LegalLink = styled.a`
    color: ${colors.blue};
    font-size: 13px;
    text-decoration: underline;
`;

export default function ResursPaymentMethods({
    totalAmount,
    selectedId,
    paymentMethods,
    onSelect
}: Props) {
    const { t } = useTranslation();

    if (!paymentMethods?.length) return null;

    return (
        <RoundBox>
            <Text i={t('payment_method')} fontWeight="bold" header />
            {paymentMethods.map((pm) => (
                <Option
                    key={pm.id}
                    selected={selectedId === pm.id}
                    onClick={() => onSelect(pm.id)}
                >
                    <RowView>
                        <RowView flexStart>
                            <Dot selected={selectedId === pm.id} />
                            <Text
                                i={getMethodLabel(pm)}
                                fontWeight={selectedId === pm.id ? 'bold' : 'normal'}
                                color={colors.dark2}
                            />
                        </RowView>
                        <ResursLogo height="22" />
                    </RowView>
                    {pm.specificType === 'PART_PAYMENT' &&
                        selectedId === pm.id &&
                        pm.legalInfoLinks?.length > 0 && (
                            <LegalLinks>
                                {pm.legalInfoLinks.map((link, i) => (
                                    <li key={i}>
                                        <LegalLink
                                            href={
                                                link.appendPriceLast
                                                    ? link.url + Math.round(totalAmount)
                                                    : link.url
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {link.endUserDescription}
                                        </LegalLink>
                                    </li>
                                ))}
                            </LegalLinks>
                        )}
                </Option>
            ))}
        </RoundBox>
    );
}
